import { type RefObject, useEffect, useRef } from 'react';

import { MouseEventType } from '@this+that/utils';

import {
  computeDragAutoScrollSpeed,
  DRAG_AUTO_SCROLL_EDGE_THRESHOLD_PX,
  DRAG_AUTO_SCROLL_MAX_SPEED_PX,
  DRAG_AUTO_SCROLL_ZERO_SPEED,
} from './dragAutoScrollMath';

const EDGE_THRESHOLD_PX = DRAG_AUTO_SCROLL_EDGE_THRESHOLD_PX;
const MAX_SCROLL_SPEED_PX = DRAG_AUTO_SCROLL_MAX_SPEED_PX;
const ZERO_SPEED = DRAG_AUTO_SCROLL_ZERO_SPEED;

type UseDragAutoScrollOptions = {
  edgeThresholdPx?: number;
  maxScrollSpeedPx?: number;
  useViewportEdges?: boolean;
};

function computeTargetSpeed(
  topDist: number,
  bottomDist: number,
  edgeThreshold: number,
  maxSpeed: number,
): number {
  return computeDragAutoScrollSpeed(
    topDist,
    bottomDist,
    edgeThreshold,
    maxSpeed,
  );
}

function startScrollLoop(
  el: HTMLElement,
  speedRef: { current: number },
  rafIdRef: { current: number | null },
): void {
  if (rafIdRef.current !== null) {
    return;
  }

  function tick(): void {
    if (speedRef.current === ZERO_SPEED) {
      rafIdRef.current = null;
      return;
    }
    el.scrollTop += speedRef.current;
    rafIdRef.current = requestAnimationFrame(tick);
  }

  rafIdRef.current = requestAnimationFrame(tick);
}

function cancelScrollLoop(
  speedRef: { current: number },
  rafIdRef: { current: number | null },
): void {
  speedRef.current = ZERO_SPEED;
  if (rafIdRef.current !== null) {
    cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = null;
  }
}

export function useDragAutoScroll(
  scrollRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  options?: UseDragAutoScrollOptions,
): void {
  const speedRef = useRef<number>(ZERO_SPEED);
  const rafIdRef = useRef<number | null>(null);

  const edgeThreshold = options?.edgeThresholdPx ?? EDGE_THRESHOLD_PX;
  const maxSpeed = options?.maxScrollSpeedPx ?? MAX_SCROLL_SPEED_PX;
  const useViewportEdges = options?.useViewportEdges ?? false;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const el = scrollRef.current;
    if (!el) {
      return;
    }

    function handleDragOver(e: DragEvent): void {
      const rect = el!.getBoundingClientRect();

      if (
        useViewportEdges &&
        (e.clientX < rect.left || e.clientX > rect.right)
      ) {
        cancelScrollLoop(speedRef, rafIdRef);
        return;
      }

      const topDist = useViewportEdges ? e.clientY : e.clientY - rect.top;
      const bottomDist = useViewportEdges
        ? window.innerHeight - e.clientY
        : rect.bottom - e.clientY;

      const targetSpeed = computeTargetSpeed(
        topDist,
        bottomDist,
        edgeThreshold,
        maxSpeed,
      );
      speedRef.current = targetSpeed;

      if (targetSpeed !== ZERO_SPEED) {
        startScrollLoop(el!, speedRef, rafIdRef);
      }
    }

    function handleDragLeave(e: DragEvent): void {
      const related = e.relatedTarget as Node | null;
      if (!related || !el!.contains(related)) {
        cancelScrollLoop(speedRef, rafIdRef);
      }
    }

    function handleStop(): void {
      cancelScrollLoop(speedRef, rafIdRef);
    }

    const dragOverTarget: EventTarget = useViewportEdges ? window : el;
    dragOverTarget.addEventListener(
      'dragover',
      handleDragOver as EventListener,
    );
    if (!useViewportEdges) {
      el.addEventListener('dragleave', handleDragLeave);
    }
    window.addEventListener('dragend', handleStop);
    window.addEventListener('drop', handleStop);
    window.addEventListener(MouseEventType.MOUSEMOVE, handleStop);

    return () => {
      dragOverTarget.removeEventListener(
        'dragover',
        handleDragOver as EventListener,
      );
      if (!useViewportEdges) {
        el.removeEventListener('dragleave', handleDragLeave);
      }
      window.removeEventListener('dragend', handleStop);
      window.removeEventListener('drop', handleStop);
      window.removeEventListener(MouseEventType.MOUSEMOVE, handleStop);
      cancelScrollLoop(speedRef, rafIdRef);
    };
  }, [scrollRef, enabled, edgeThreshold, maxSpeed, useViewportEdges]);
}
